import React, { useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";

interface TrendItem {
  date: string;
  bannerViews: number;
  bannerClicks: number;
  productViews: number;
  searches: number;
}

interface AnalyticsTrendChartProps {
  trend?: TrendItem[];
}

type MetricKey = "bannerViews" | "bannerClicks" | "productViews" | "searches";

interface MetricConfig {
  key: MetricKey;
  label: string;
}

const metrics: MetricConfig[] = [
  {
    key: "bannerViews",
    label: "Banner Views",
  },
  {
    key: "bannerClicks",
    label: "Banner Clicks",
  },
  {
    key: "productViews",
    label: "Product Views",
  },
  {
    key: "searches",
    label: "Searches",
  },
];

const AnalyticsTrendChart: React.FC<AnalyticsTrendChartProps> = ({
  trend = [],
}) => {
  const [activeMetric, setActiveMetric] = useState<MetricKey>("bannerViews");

  const activeConfig = metrics.find((metric) => metric.key === activeMetric);

  const chartData = useMemo(() => {
    return trend.map((item) => ({
      ...item,
      value: Number(item[activeMetric] ?? 0),
    }));
  }, [trend, activeMetric]);

  const maxValue = useMemo(() => {
    const max = Math.max(...chartData.map((item) => item.value), 0);

    return max === 0 ? 1 : max;
  }, [chartData]);

  const chartWidth = 900;
  const chartHeight = 300;

  const paddingLeft = 55;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 45;

  const graphWidth = chartWidth - paddingLeft - paddingRight;

  const graphHeight = chartHeight - paddingTop - paddingBottom;

  const points = chartData.map((item, index) => {
    const x =
      chartData.length === 1
        ? paddingLeft + graphWidth / 2
        : paddingLeft + (index / (chartData.length - 1)) * graphWidth;

    const y = paddingTop + graphHeight - (item.value / maxValue) * graphHeight;

    return {
      ...item,
      x,
      y,
    };
  });

  const linePath =
    points.length > 0
      ? points
          .map(
            (point, index) =>
              `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`,
          )
          .join(" ")
      : "";

  const areaPath =
    points.length > 0
      ? `${linePath}
         L ${points[points.length - 1].x} ${paddingTop + graphHeight}
         L ${points[0].x} ${paddingTop + graphHeight}
         Z`
      : "";

  const formatDate = (date: string) => {
    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleDateString("en-AE", {
      day: "2-digit",
      month: "short",
    });
  };

  const formatValue = (value: number) => value.toLocaleString();

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      {/* HEADER */}

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <TrendingUp size={19} />
          </div>

          <div>
            <h2 className="text-sm font-bold text-gray-800">
              Engagement Trend
            </h2>

            <p className="text-[11px] text-gray-400 mt-0.5">
              Customer activity over the selected period
            </p>
          </div>
        </div>

        {/* METRIC SWITCHER */}

        <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1 overflow-x-auto">
          {metrics.map((metric) => {
            const active = activeMetric === metric.key;

            return (
              <button
                key={metric.key}
                type="button"
                onClick={() => setActiveMetric(metric.key)}
                className={`
                  whitespace-nowrap
                  px-3
                  py-1.5
                  rounded-lg
                  text-[10px]
                  font-semibold
                  transition-all
                  duration-200
                  ${
                    active
                      ? "bg-white text-logoHeading shadow-sm"
                      : "text-gray-400 hover:text-gray-600"
                  }
                `}
              >
                {metric.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* CHART */}

      {chartData.length === 0 ? (
        <div className="h-[300px] flex items-center justify-center">
          <p className="text-xs text-gray-400">
            No engagement data available for this period.
          </p>
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <svg
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="w-full min-w-[650px] h-[300px]"
            preserveAspectRatio="none"
          >
            {/* GRID */}

            {[0, 1, 2, 3, 4].map((index) => {
              const y = paddingTop + (graphHeight / 4) * index;

              const value = maxValue - (maxValue / 4) * index;

              return (
                <React.Fragment key={index}>
                  <line
                    x1={paddingLeft}
                    x2={chartWidth - paddingRight}
                    y1={y}
                    y2={y}
                    stroke="#eef1f4"
                    strokeWidth="1"
                  />

                  <text
                    x={paddingLeft - 10}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="10"
                    fill="#9ca3af"
                  >
                    {Math.round(value).toLocaleString()}
                  </text>
                </React.Fragment>
              );
            })}

            {/* AREA */}

            {areaPath && <path d={areaPath} fill="rgba(47, 108, 55, 0.08)" />}

            {/* LINE */}

            {linePath && (
              <path
                d={linePath}
                fill="none"
                stroke="#2f6c37"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* POINTS */}

            {points.map((point, index) => (
              <g key={`${point.date}-${index}`}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="5"
                  fill="white"
                  stroke="#2f6c37"
                  strokeWidth="2"
                />

                <text
                  x={point.x}
                  y={point.y - 12}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="600"
                  fill="#374151"
                >
                  {formatValue(point.value)}
                </text>

                <text
                  x={point.x}
                  y={chartHeight - 15}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#9ca3af"
                >
                  {formatDate(point.date)}
                </text>
              </g>
            ))}
          </svg>
        </div>
      )}

      {/* FOOTER */}

      {activeConfig && chartData.length > 0 && (
        <div className="mt-3 flex items-center gap-2 text-[10px] text-gray-400">
          <span className="w-2 h-2 rounded-full bg-logoHeading" />

          <span>
            Showing {activeConfig.label.toLowerCase()} for {chartData.length}{" "}
            day
            {chartData.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTrendChart;
